import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Route as RouteIcon, Building2, CalendarDays, FileText, LogOut, ExternalLink, Mails, MapPinned, Menu, X } from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { ASSETS } from "@/lib/api";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/rotas", label: "Rotas", icon: RouteIcon },
  { to: "/admin/cidades", label: "Cidades", icon: Building2 },
  { to: "/admin/eventos", label: "Eventos", icon: CalendarDays },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/sugestoes", label: "Sugestões de Rotas", icon: MapPinned },
  { to: "/admin/inscritos", label: "Inscritos", icon: Mails },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Fecha o drawer ao trocar de rota
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Trava scroll do body enquanto drawer aberto (mobile)
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#0B0B0B] text-white" data-testid="admin-layout">
      {/* Mobile top bar */}
      <header
        data-testid="admin-topbar"
        className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a] bg-[#0d0d0d]/95 backdrop-blur"
      >
        <Link to="/admin" className="flex items-center gap-2.5 min-w-0">
          <img src={ASSETS.logoMark} alt="ROTTA" className="h-7 w-auto" />
          <div className="flex flex-col leading-none min-w-0">
            <span className="font-display text-base tracking-wider truncate">ROTTA</span>
            <span className="text-[9px] tracking-[0.25em] text-[#C6A762]">PAINEL ADMIN</span>
          </div>
        </Link>
        <button
          data-testid="admin-drawer-toggle"
          onClick={() => setDrawerOpen(true)}
          className="p-2 -mr-2 text-white/90 hover:text-[#C6A762]"
          aria-label="Abrir menu"
        >
          <Menu size={22} />
        </button>
      </header>

      {/* Sidebar — desktop persistent, mobile drawer */}
      <>
        {/* Backdrop (mobile only) */}
        {drawerOpen && (
          <button
            aria-label="Fechar menu"
            onClick={() => setDrawerOpen(false)}
            data-testid="admin-drawer-backdrop"
            className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          />
        )}
        <aside
          data-testid="admin-sidebar"
          className={`
            fixed lg:sticky lg:top-0 z-50 lg:z-auto
            top-0 left-0 h-[100dvh] lg:h-screen w-[280px] lg:w-[260px] shrink-0
            border-r border-[#1a1a1a] flex flex-col bg-[#0d0d0d]
            transition-transform duration-300 ease-out
            ${drawerOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          `}
        >
          <div className="p-5 lg:p-6 border-b border-[#1a1a1a] flex items-center justify-between gap-3">
            <Link to="/admin" className="flex items-center gap-3 min-w-0">
              <img src={ASSETS.logoMark} alt="ROTTA" className="h-9 w-auto" />
              <div className="flex flex-col leading-none min-w-0">
                <span className="font-display text-lg tracking-wider truncate">ROTTA</span>
                <span className="text-[9px] tracking-[0.3em] text-[#C6A762]">PAINEL ADMIN</span>
              </div>
            </Link>
            <button
              onClick={() => setDrawerOpen(false)}
              className="lg:hidden p-2 -mr-1 text-white/70 hover:text-[#C6A762]"
              aria-label="Fechar menu"
              data-testid="admin-drawer-close"
            >
              <X size={20} />
            </button>
          </div>

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`admin-nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition ${
                    isActive
                      ? "bg-[#C6A762]/10 text-[#C6A762] border border-[#C6A762]/30"
                      : "text-[#B8B8B8] hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <it.icon size={16} />
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="p-3 border-t border-[#1a1a1a] space-y-1">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-xs text-[#B8B8B8] hover:text-[#C6A762] hover:bg-white/5">
              <ExternalLink size={14} /> Ver site público
            </a>
            <div className="px-4 py-3 rounded-lg bg-[#141414] border border-[#1f1f1f]">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#7d7d7d]">Logado como</div>
              <div className="text-sm text-white mt-1 truncate">{user?.email}</div>
            </div>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-xs text-[#B8B8B8] hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut size={14} /> Sair
            </button>
          </div>
        </aside>
      </>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-12 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
